import { useParams, useLocation, Link, Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import Spinner from '../../../../Shared/Spinner';
import LegStepIndicator from '../../../Shared/LegStepIndicator';
import InformationBox from '../../../Shared/InformationBox';
import Breadcumb from '../../../Shared/Breadcrumb';
import penIcon from '../../../../../../assets/icons/pen-default-blue-1000.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import LinkToParticipationOverviewPuff from '../../../Shared/LinkToParticipationOverviewPuff';
import ParticipationResultApiModel from '../../../models/participation-result.api-model';
import type { TeacherApiModel } from '../../../models/teacher.api-model';
import type { IParticipationResult } from '../../../../../models/pa-egna-ben/participation-result';
import PageIntroduction from '../../../Shared/PageIntroduction';
import { Role } from '../../../../../models/pa-egna-ben/role.enum';
import NotFoundRedirect from '../../../Shared/NotFoundRedirect';
import { InformationBoxType } from '../../../Shared/InformationBox/information-box-type.enum';

function processRecievedData(data: ParticipationResultApiModel): IParticipationResult {
  return ParticipationResultApiModel.mapToBusinessModel(data);
}

function Participation() {
  // === HOOKS ===
  const { participationId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // === STATE ===
  const [getParticipationResult] = useFetch<ParticipationResultApiModel>(`/api/paegnaben/municipality-administrator/participations/${participationId}/result`);
  const [getTeacher] = useFetch<TeacherApiModel[]>(`/api/paegnaben/municipality-administrator/participations/${participationId}/teachers`);

  // === VARIABLES ===
  const participationResult = getParticipationResult.data ? processRecievedData(getParticipationResult.data) : undefined;

  // === RENDER ===
  if (getParticipationResult.httpResponseStatusCode === 401 || getTeacher.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getParticipationResult.httpResponseStatusCode === 403 || getTeacher.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (getParticipationResult.httpResponseStatusCode === 404 || getTeacher.httpResponseStatusCode === 404) {
    return <NotFoundRedirect />;
  }

  return (
    <div>
      {getParticipationResult.status === Status.loading && <Spinner />}
      {getParticipationResult.status === Status.success && participationResult && (
        <div>
          <Breadcumb
            items={[
              { text: 'Mina sidor', url: '/' },
              { text: `${participationResult.municipalityName}`, url: `/kommuner/${getParticipationResult.data?.municipalityGid}` },
              { text: `${participationResult.participationName}`, url: undefined },
            ]}
          />
          <PageIntroduction heading={participationResult?.participationName} />
          <div className="mb-4 mb-md-5">
            {queryParams.get('a') === 'poang-registrerade' && (
              <InformationBox type={InformationBoxType.Success} headingElement={<span className="fw-bold">Poäng har rapporterats</span>} className="mb-4 mb-md-5" setFocus={true} />
            )}

            {queryParams.get('a') === 'klass-sparad' && (
              <InformationBox type={InformationBoxType.Success} headingElement={<span className="fw-bold">Ändringar har sparats</span>} className="mb-4 mb-md-5" setFocus={true} />
            )}
            <LegStepIndicator
              participationId={Number(participationId)}
              competitionLegs={participationResult.legs}
              legResults={participationResult.legResults}
              viewerRole={Role.municipalityAdministrator}
            />
          </div>

          {getTeacher.status === Status.loading && <Spinner />}
          {getTeacher.status === Status.success && getTeacher.data && (
            <InformationBox
              type={InformationBoxType.Default}
              headingElement={<h2 className="h4 mb-2">Kontaktuppgifter till pedagog</h2>}
              textElement={
                <>
                  {getTeacher.data.length === 0 ? (
                    <div className="mb-2">Pedagog saknas</div>
                  ) : (
                    <ul className="list-unstyled mb-2">
                      {getTeacher.data.map((x, i, row) => (
                        <li key={x.emailAddress} className={i + 1 === row.length ? '' : 'mb-2'}>
                          <div>
                            {x.firstName} {x.lastName}
                          </div>
                          <div>{x.emailAddress}</div>
                          {x.phoneNumber && <div>{x.phoneNumber}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link to={`/klasser/${participationId}/klassuppgifter`} className="d-flex align-items-center">
                    <img src={DataUrlHelper.encode(penIcon)} alt="" className="me-1" /> Klassuppgifter
                  </Link>
                </>
              }
              className="mb-4 mb-md-5"
            />
          )}
          <LinkToParticipationOverviewPuff participationId={Number(participationId)} className="mt-4 mt-md-5" />
        </div>
      )}
    </div>
  );
}

export default Participation;
